// Shared polling cadence for the leaderboard API pollers (tournaments list,
// tournament results, scoring status), mirroring the legacy leaderboard JS
// (tournaments2.js.coffee): reset to the floor when the last poll brought
// fresh data, otherwise back off exponentially when the customer's polling
// optimization is active, or linearly (+5s per quiet tick) when it is not.

// Legacy `incremental_poll`: the linear cadence's growth per quiet tick.
export const LINEAR_POLL_INCREMENT_MS = 5000;

// Legacy `gon.maximum_poll || 60000`: the cap when the backend sends none.
// Customer#leaderboard_polling_settings now always sends one, so this only
// guards an initial state built by an older backend.
export const DEFAULT_POLL_MAX_INTERVAL_MS = 60000;

// Whether the customer's polling optimization (multiplicative back-off with a
// stop rule) is off: legacy `!multiplier_active`. The backend sends no
// multiplier in that case, so a missing multiplier means the same thing.
const isOptimizationDisabled = ({ incrementalWaitTimeDisabled, pollMultiplier }) =>
  Boolean(incrementalWaitTimeDisabled) || !pollMultiplier;

export function nextPollInterval(interval, changed, leaderboardV2State) {
  const { pollMinInterval, pollMaxInterval, pollMultiplier } = leaderboardV2State;

  if (changed) return pollMinInterval;

  const maxInterval = pollMaxInterval ?? DEFAULT_POLL_MAX_INTERVAL_MS;

  if (isOptimizationDisabled(leaderboardV2State)) {
    return Math.min(interval + LINEAR_POLL_INCREMENT_MS, maxInterval);
  }

  return Math.min(interval * pollMultiplier, maxInterval);
}

// Legacy stop rule (`ongoing_tournament || !multiplier_active`): pollers keep
// rescheduling while the round is ongoing (in progress with play or score
// activity in the last 24h) - and always, whatever the round's state, for
// customers whose polling optimization is disabled: their leaderboards never
// stop refreshing while the page is open. The ongoing flag is seeded for the
// current round in the initial state and refreshed from the round_ongoing
// field of every tournaments response; a round whose flag is still unknown
// keeps polling until a response says otherwise. Once a round's pollers have
// stopped, polling only resumes on a round switch or a page reload.
export function shouldContinuePolling(leaderboardV2State) {
  if (isOptimizationDisabled(leaderboardV2State)) return true;

  const { roundId, roundList } = leaderboardV2State;

  const round = roundList?.find((r) => String(r.id) === String(roundId));
  return round?.ongoing !== false;
}
